// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getDocument } from "pdfjs-dist/webpack";

import { APIS_RPRTPROXY } from "@/tz/go/egaz/osci/comn/rprt/services/OsciComnRprtProxyService";

/**
 * QR(원본확인) 스캔 시 열리는 공개 PDF 뷰어 (모바일 대응).
 *
 * - iOS Safari: 인라인 PDF를 잘 지원하므로 Blob을 <object>에 띄워 네이티브 PDF 뷰어로 표시(핀치줌·선명).
 * - Android Chrome/기타: 인라인 PDF 미지원이라 pdf.js로 직접 canvas 렌더한다.
 *   공통 Pdf의 흐림 원인이던 devicePixelRatio를 반영해 선명하게, 화면 폭 100%, 전 페이지 세로 스크롤.
 * 공통 Pdf 컴포넌트는 사용/수정하지 않으므로 데스크톱 보고서 화면에는 영향이 없다.
 */
const isIOS = () =>
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS는 데스크톱 Safari로 위장(MacIntel + 터치)
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const OsciPdfViewer = () => {
    const { origCfrmUuid } = useParams();
    const containerRef = useRef<HTMLDivElement>(null);
    const [pdfUrl, setPdfUrl] = useState<string>(); // iOS 네이티브 <object>용 Blob URL
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!origCfrmUuid) return;

        let cancelled = false;
        let objectUrl: string | undefined;
        let pdfDoc: any;

        const run = async () => {
            try {
                const res: any = await APIS_RPRTPROXY.getRprtProxy(origCfrmUuid);
                if (cancelled) return;

                if (isIOS()) {
                    // iOS: 네이티브 PDF 뷰어 (응답이 attachment라 URL 직접삽입은 다운로드되므로 Blob으로 감쌈)
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    objectUrl = URL.createObjectURL(blob);
                    setPdfUrl(objectUrl);
                    return;
                }

                // Android/기타: pdf.js로 DPR 반영해 선명하게 canvas 렌더
                const bytes = new Uint8Array(res.data);
                pdfDoc = await getDocument({ data: bytes }).promise;
                if (cancelled) return;

                const container = containerRef.current;
                if (!container) return;
                container.innerHTML = "";

                const dpr = window.devicePixelRatio || 1;
                const cssWidth = container.clientWidth || window.innerWidth;

                for (let i = 1; i <= pdfDoc.numPages; i++) {
                    const page = await pdfDoc.getPage(i);
                    if (cancelled) return;

                    const base = page.getViewport({ scale: 1 });
                    // 백킹스토어는 DPR까지 곱해 고해상도(선명), CSS 폭은 화면 폭에 맞춤
                    const viewport = page.getViewport({ scale: (cssWidth / base.width) * dpr });

                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.style.width = "100%";
                    canvas.style.height = "auto";
                    canvas.style.display = "block";
                    canvas.style.marginBottom = "8px";
                    container.appendChild(canvas);

                    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
                }
            } catch (e) {
                if (!cancelled) setError(true);
            }
        };

        run();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            if (pdfDoc && pdfDoc.destroy) pdfDoc.destroy();
        };
    }, [origCfrmUuid]);

    if (error) {
        return <div style={{ padding: 16 }}>PDF를 불러오지 못했습니다.</div>;
    }

    if (pdfUrl) {
        // iOS: 네이티브 PDF 뷰어
        return (
            <div style={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column" }}>
                <object data={pdfUrl} type="application/pdf" style={{ flex: 1, width: "100%", border: "none" }}>
                    <div style={{ padding: 16 }}>
                        이 브라우저에서 PDF를 표시할 수 없습니다.{" "}
                        <a href={pdfUrl} target="_blank" rel="noreferrer">
                            PDF 열기
                        </a>
                    </div>
                </object>
            </div>
        );
    }

    // Android/기타: pdf.js canvas 렌더 컨테이너 (로딩 중에도 이 컨테이너가 마운트되어 있어야 함)
    return <div ref={containerRef} style={{ width: "100%", minHeight: "100vh", background: "#525659" }} />;
};

export default OsciPdfViewer;
